/**
 * Skin Registry
 *
 * Maps skin IDs to their pre-created component references.
 * Components are imported at module level for stable references.
 */

import type {
  SkinId,
  SkinBaseComponents,
  SkinPawnComponents,
  SkinDiceComponents,
  SkinPseudoComponents,
  SkinPlateauxComponents
} from './types';

import {
  RedBase as BasicsRedBase,
  BlueBase as BasicsBlueBase,
  YellowBase as BasicsYellowBase,
  GreenBase as BasicsGreenBase
} from '../bases/basics/baseFactory';

import {
  RedBase as CelesteRedBase,
  BlueBase as CelesteBlueBase,
  YellowBase as CelesteYellowBase,
  GreenBase as CelesteGreenBase
} from '../bases/celeste/baseFactory';

import {
  RedPawn as BasicsRedPawn,
  BluePawn as BasicsBluePawn,
  YellowPawn as BasicsYellowPawn,
  GreenPawn as BasicsGreenPawn
} from '../pawns/basics/pawnFactory';

import {
  RedPawn as CelesteRedPawn,
  BluePawn as CelesteBluePawn,
  YellowPawn as CelesteYellowPawn,
  GreenPawn as CelesteGreenPawn
} from '../pawns/celeste/pawnFactory';

import {
  RedPawn as CryptRedPawn,
  BluePawn as CryptBluePawn,
  YellowPawn as CryptYellowPawn,
  GreenPawn as CryptGreenPawn
} from '../pawns/crypt/pawnFactory';

import {
  RedDice as BasicsRedDice,
  BlueDice as BasicsBlueDice,
  YellowDice as BasicsYellowDice,
  GreenDice as BasicsGreenDice
} from '../dice/basics/diceFactory';

import {
  RedDice as CelesteRedDice,
  BlueDice as CelesteBlueDice,
  YellowDice as CelesteYellowDice,
  GreenDice as CelesteGreenDice
} from '../dice/celeste/diceFactory';

import {
  RedDice as CryptRedDice,
  BlueDice as CryptBlueDice,
  YellowDice as CryptYellowDice,
  GreenDice as CryptGreenDice
} from '../dice/crypt/diceFactory';

import {
  LeftPseudo as BasicsLeftPseudo,
  RightPseudo as BasicsRightPseudo
} from '../pseudo/basics/pseudoFactory';
import {
  LeftPseudo as CelesteLeftPseudo,
  RightPseudo as CelesteRightPseudo
} from '../pseudo/celeste/pseudoFactory';

import {
  LeftRoadSection as BasicsLeftRoadSection,
  TopRoadSection as BasicsTopRoadSection,
  RightRoadSection as BasicsRightRoadSection,
  BottomRoadSection as BasicsBottomRoadSection
} from '../plateaux/basics/plateauxFactory';

import {
  LeftRoadSection as CelesteLeftRoadSection,
  TopRoadSection as CelesteTopRoadSection,
  RightRoadSection as CelesteRightRoadSection,
  BottomRoadSection as CelesteBottomRoadSection
} from '../plateaux/celeste/plateauxFactory';

import {
  LeftRoadSection as CryptLeftRoadSection,
  TopRoadSection as CryptTopRoadSection,
  RightRoadSection as CryptRightRoadSection,
  BottomRoadSection as CryptBottomRoadSection
} from '../plateaux/crypt/plateauxFactory';

import {
  RedBase as CryptRedBase,
  BlueBase as CryptBlueBase,
  YellowBase as CryptYellowBase,
  GreenBase as CryptGreenBase
} from '../bases/crypt/baseFactory';

import {
  RedBase as MagmaRedBase,
  BlueBase as MagmaBlueBase,
  YellowBase as MagmaYellowBase,
  GreenBase as MagmaGreenBase
} from '../bases/magma/baseFactory';

import {
  RedPawn as MagmaRedPawn,
  BluePawn as MagmaBluePawn,
  YellowPawn as MagmaYellowPawn,
  GreenPawn as MagmaGreenPawn
} from '../pawns/magma/pawnFactory';

import {
  RedDice as MagmaRedDice,
  BlueDice as MagmaBlueDice,
  YellowDice as MagmaYellowDice,
  GreenDice as MagmaGreenDice
} from '../dice/magma/diceFactory';

import {
  LeftPseudo as CryptLeftPseudo,
  RightPseudo as CryptRightPseudo
} from '../pseudo/crypt/pseudoFactory';

import {
  LeftPseudo as MagmaLeftPseudo,
  RightPseudo as MagmaRightPseudo
} from '../pseudo/magma/pseudoFactory';

import {
  LeftPseudo as PcLeftPseudo,
  RightPseudo as PcRightPseudo
} from '../pseudo/pc/pseudoFactory';

import {
  LeftRoadSection as MagmaLeftRoadSection,
  TopRoadSection as MagmaTopRoadSection,
  RightRoadSection as MagmaRightRoadSection,
  BottomRoadSection as MagmaBottomRoadSection
} from '../plateaux/magma/plateauxFactory';

import {
  RedBase as PcRedBase,
  BlueBase as PcBlueBase,
  YellowBase as PcYellowBase,
  GreenBase as PcGreenBase
} from '../bases/pc/baseFactory';

import {
  RedPawn as PcRedPawn,
  BluePawn as PcBluePawn,
  YellowPawn as PcYellowPawn,
  GreenPawn as PcGreenPawn
} from '../pawns/pc/pawnFactory';

import {
  RedDice as PcRedDice,
  BlueDice as PcBlueDice,
  YellowDice as PcYellowDice,
  GreenDice as PcGreenDice
} from '../dice/pc/diceFactory';

import {
  LeftRoadSection as PcLeftRoadSection,
  TopRoadSection as PcTopRoadSection,
  RightRoadSection as PcRightRoadSection,
  BottomRoadSection as PcBottomRoadSection
} from '../plateaux/pc/plateauxFactory';

const SKIN_BASES: Record<SkinId, SkinBaseComponents> = {
  basics: {
    red: BasicsRedBase,
    blue: BasicsBlueBase,
    yellow: BasicsYellowBase,
    green: BasicsGreenBase
  },
  celeste: {
    red: CelesteRedBase,
    blue: CelesteBlueBase,
    yellow: CelesteYellowBase,
    green: CelesteGreenBase
  },
  crypt: {
    red: CryptRedBase,
    blue: CryptBlueBase,
    yellow: CryptYellowBase,
    green: CryptGreenBase
  },
  magma: {
    red: MagmaRedBase,
    blue: MagmaBlueBase,
    yellow: MagmaYellowBase,
    green: MagmaGreenBase
  },
  pc: {
    red: PcRedBase,
    blue: PcBlueBase,
    yellow: PcYellowBase,
    green: PcGreenBase
  }
};

const SKIN_PAWNS: Record<SkinId, SkinPawnComponents> = {
  basics: {
    red: BasicsRedPawn,
    blue: BasicsBluePawn,
    yellow: BasicsYellowPawn,
    green: BasicsGreenPawn
  },
  celeste: {
    red: CelesteRedPawn,
    blue: CelesteBluePawn,
    yellow: CelesteYellowPawn,
    green: CelesteGreenPawn
  },
  crypt: {
    red: CryptRedPawn,
    blue: CryptBluePawn,
    yellow: CryptYellowPawn,
    green: CryptGreenPawn
  },
  magma: {
    red: MagmaRedPawn,
    blue: MagmaBluePawn,
    yellow: MagmaYellowPawn,
    green: MagmaGreenPawn
  },
  pc: {
    red: PcRedPawn,
    blue: PcBluePawn,
    yellow: PcYellowPawn,
    green: PcGreenPawn
  }
};

const SKIN_DICE: Record<SkinId, SkinDiceComponents> = {
  basics: {
    red: BasicsRedDice,
    blue: BasicsBlueDice,
    yellow: BasicsYellowDice,
    green: BasicsGreenDice
  },
  celeste: {
    red: CelesteRedDice,
    blue: CelesteBlueDice,
    yellow: CelesteYellowDice,
    green: CelesteGreenDice
  },
  crypt: {
    red: CryptRedDice,
    blue: CryptBlueDice,
    yellow: CryptYellowDice,
    green: CryptGreenDice
  },
  magma: {
    red: MagmaRedDice,
    blue: MagmaBlueDice,
    yellow: MagmaYellowDice,
    green: MagmaGreenDice
  },
  pc: {
    red: PcRedDice,
    blue: PcBlueDice,
    yellow: PcYellowDice,
    green: PcGreenDice
  }
};

const SKIN_PSEUDO: Record<SkinId, SkinPseudoComponents> = {
  basics: { left: BasicsLeftPseudo, right: BasicsRightPseudo },
  celeste: { left: CelesteLeftPseudo, right: CelesteRightPseudo },
  crypt: { left: CryptLeftPseudo, right: CryptRightPseudo },
  magma: { left: MagmaLeftPseudo, right: MagmaRightPseudo },
  pc: { left: PcLeftPseudo, right: PcRightPseudo }
};

export function getSkinBases(skinId: SkinId): SkinBaseComponents {
  return SKIN_BASES[skinId];
}

export function getSkinPawns(skinId: SkinId): SkinPawnComponents {
  return SKIN_PAWNS[skinId];
}

export function getSkinDice(skinId: SkinId): SkinDiceComponents {
  return SKIN_DICE[skinId];
}

export function getSkinPseudo(skinId: SkinId): SkinPseudoComponents {
  return SKIN_PSEUDO[skinId];
}

const SKIN_PLATEAUX: Record<SkinId, SkinPlateauxComponents> = {
  basics: {
    left: BasicsLeftRoadSection,
    top: BasicsTopRoadSection,
    right: BasicsRightRoadSection,
    bottom: BasicsBottomRoadSection
  },
  celeste: {
    left: CelesteLeftRoadSection,
    top: CelesteTopRoadSection,
    right: CelesteRightRoadSection,
    bottom: CelesteBottomRoadSection
  },
  crypt: {
    left: CryptLeftRoadSection,
    top: CryptTopRoadSection,
    right: CryptRightRoadSection,
    bottom: CryptBottomRoadSection
  },
  magma: {
    left: MagmaLeftRoadSection,
    top: MagmaTopRoadSection,
    right: MagmaRightRoadSection,
    bottom: MagmaBottomRoadSection
  },
  pc: {
    left: PcLeftRoadSection,
    top: PcTopRoadSection,
    right: PcRightRoadSection,
    bottom: PcBottomRoadSection
  }
};

export function getSkinPlateaux(skinId: SkinId): SkinPlateauxComponents {
  return SKIN_PLATEAUX[skinId];
}
